import React, { useEffect, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { toDateSafe } from '@utils/Date';
import { getOrderChannel } from '@utils/OrdersBoard';
import { can, Capability } from '@utils/Permissions/Capabilities';
import { canCurrentUser } from '@utils/Permissions';
import OrdersService from '@api/Orders/OrderCtrl';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { ProfileTypes } from '@api/Users/User';
import RewardsService from '@api/Rewards/RewardCtrl';
import { Reward } from '@api/Rewards/Reward';
import { ModalRegisterPayment } from '@components/Collaborator/Modals/ModalRegisterPayment';
import { ModalShowTicket } from '@components/Collaborator/Modals/ModalShowTicket';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import { ModalUberQuote } from '@components/Modals/ModalUberQuote';
import useOpenModal from '@hooks/useOpenModal';
import OrdersBoardView from '@screens/Orders/components/OrdersBoardView';
import { OrdersView, getStoredOrdersView, storeOrdersView } from '@screens/Orders/components/OrdersViewSwitch';
import OrdersListColumn from '@screens/Orders/components/OrdersListColumn';
import OrderDetailPanel from '@screens/Orders/components/OrderDetailPanel';
import OrderProductsPanel from '@screens/Orders/components/OrderProductsPanel';
import { EmptyOrderState } from '@screens/Orders/components/OrderEmptyStates';
import useOrdersFeed from '@screens/Orders/hooks/useOrdersFeed';
import useSelectedOrder from '@screens/Orders/hooks/useSelectedOrder';
import useOrderPosNavigation from '@screens/Orders/hooks/useOrderPosNavigation';
import useOrderActions from '@screens/Orders/hooks/useOrderActions';
import useUberDispatch from '@screens/Orders/hooks/useUberDispatch';

// ============================================================================
// `/orders`: la pantalla de pedidos en tiempo real, con dos vistas —Lista y
// Tablero de cocina— sobre los MISMOS datos y las MISMAS acciones.
//
// Aquí solo se arma: cada responsabilidad vive en su pieza.
// · `useOrdersFeed`          la ventana realtime, filtros de pantalla, sonidos.
// · `useSelectedOrder`       el pedido abierto, `?orderId=` y el borrador del link.
// · `useOrderActions`        lo que mueve un pedido; el ÚNICO ejecutor del cierre
//                            y el gate del cobro (lo fija `completePaths.test.ts`).
// · `useOrderPosNavigation`  retomar en el POS y cerrar una mesa.
// · `useUberDispatch`        mandar un delivery con Uber Direct.
// · `components/*`           la lista, el detalle, las líneas; presentacionales.
// ============================================================================
const ListOrdersScreen: React.FunctionComponent = () => {
    const { loggedUser, profileSelected } = useSelector((state: RootReducer) => state.auth);
    const ordersService = useMemo(() => new OrdersService(), []);
    const rewardsService = useMemo(() => new RewardsService(), []);

    // Los perfiles se comparan SOLO aquí (el trinquete de `profileChecks.test.ts`
    // cuenta este archivo); las piezas reciben el resultado.
    // · Perfil REAL: marcar "Pagado" a mano y "Eliminar".
    const isAdmin = loggedUser?.profile.profile === ProfileTypes.Admin;
    // · Perfil SELECCIONADO: a dónde se retoma un pedido (`pos` para el admin,
    //   el home —que ES el POS— para manager/colaborador).
    const posRoute = profileSelected === ProfileTypes.Admin ? '../pos' : '../home';

    const { orders, search, setSearch, hideTodayLongTime, setHideTodayLongTime, filters, setFilters } =
        useOrdersFeed(ordersService);
    const { currentOrder, setCurrentOrder, routeUrlDraft, setRouteUrlDraft } = useSelectedOrder(orders);
    const [reward, setReward] = useState<Reward | undefined>(undefined);
    // Lista (la de siempre) o tablero de cocina. Se recuerda por dispositivo:
    // la pantalla de cocina se queda en tablero entre recargas.
    const [view, setView] = useState<OrdersView>(getStoredOrdersView());
    const modalShowTicket = useOpenModal();

    const onChangeView = (nextView: OrdersView) => {
        setView(nextView);
        storeOrdersView(nextView);
    };

    const { navigateToPosForOrder, onConfirmCloseOrdersByTable, onModifyOrder } = useOrderPosNavigation(
        ordersService,
        posRoute,
        currentOrder
    );
    const actions = useOrderActions({
        ordersService,
        currentOrder,
        setCurrentOrder,
        routeUrlDraft,
        navigateToPosForOrder,
        onConfirmCloseOrdersByTable,
        onChangeView
    });
    const uber = useUberDispatch(currentOrder);

    const getData = async () => {
        showLoader();
        try {
            const dataRewards = await rewardsService.getRewards();
            setReward(dataRewards);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        // Una lectura al montar (`reward` es un singleton); nunca lanza: el
        // error ya se muestra adentro.
        void getData();
    }, []);

    // Tablero de cocina: misma data realtime, otra forma de operarla.
    // Comparte el buscador y las acciones con la lista; el modal de cancelar es
    // el único que necesita (los flujos de Uber/recibo viven en la lista).
    if (view === 'board') {
        return (
            <>
                <OrdersBoardView
                    orders={orders}
                    search={search}
                    onChangeSearch={setSearch}
                    view={view}
                    onChangeView={onChangeView}
                    canMarkPaid={isAdmin}
                    onAction={actions.onBoardAction}
                />
                {/* El cobro que faltaba antes de completar. NO lleva el guard de
                `currentOrder`: el tablero completa desde la tarjeta, sin
                seleccionar el pedido — el pedido en cuestión vive en el ref. */}
                <ModalRegisterPayment
                    modalRegisterPayment={actions.modalRegisterPayment}
                    order={actions.pedidoPorCobrar?.order}
                    onRegister={actions.onRegisterPayment}
                    onSkip={actions.onSkipPayment}
                />
                <ModalConfirmation modalConfirmation={actions.modalCancel} onConfirmation={actions.onCancelOrder} />
            </>
        );
    }

    return (
        <Grid
            container
            sx={{
                height: { xs: 'auto', md: '100%' },
                backgroundColor: 'lightgrey'
            }}
        >
            <Grid item xs={12} md={3.5} bgcolor="white" height="100%" overflow="auto">
                <OrdersListColumn
                    orders={orders}
                    currentOrder={currentOrder}
                    onSelectOrder={setCurrentOrder}
                    search={search}
                    onChangeSearch={setSearch}
                    view={view}
                    onChangeView={onChangeView}
                    filters={filters}
                    onChangeFilters={setFilters}
                    hideTodayLongTime={hideTodayLongTime}
                    onToggleHideTodayLongTime={() => setHideTodayLongTime(!hideTodayLongTime)}
                    canHideLongRunning={can({ profile: loggedUser?.profile.profile }, Capability.HideLongRunningOrders)}
                />
            </Grid>
            <Grid item xs={12} md={3.5} height="100%" overflow="auto">
                <Stack height="100%">
                    {!currentOrder && (
                        <Stack flex={1} m={2}>
                            <EmptyOrderState />
                        </Stack>
                    )}
                    {currentOrder && (
                        <OrderDetailPanel
                            order={currentOrder}
                            reward={reward}
                            canMarkPaid={isAdmin}
                            routeUrlDraft={routeUrlDraft}
                            onChangeRouteUrlDraft={setRouteUrlDraft}
                            onChangeStatusOrder={actions.onChangeStatusOrder}
                            onConfirmCancelOrder={actions.onConfirmCancelOrder}
                            onSendWithUber={uber.onSendWithUber}
                            onRouteOrder={actions.onRouteOrder}
                            onConfirmCloseOrdersByTable={() => onConfirmCloseOrdersByTable()}
                        />
                    )}
                </Stack>
            </Grid>
            <Grid item xs={12} md={5} height="100%" overflow="auto">
                <Stack height="100%">
                    {!currentOrder && (
                        <Stack flex={1} m={2} ml={0}>
                            <EmptyOrderState />
                        </Stack>
                    )}
                    {currentOrder && (
                        <OrderProductsPanel
                            order={currentOrder}
                            // Los dos admins; el de sucursal, solo pedidos de las suyas.
                            canPurge={canCurrentUser(Capability.OrdersPurge, {
                                branchOfficeId: currentOrder.branchOfficeId || 'sin-sucursal'
                            })}
                            // El tablero es realtime y se actualiza solo: basta soltar la selección.
                            onPurged={() => setCurrentOrder(undefined)}
                            onShowTicket={() => modalShowTicket.toggle()}
                            onModifyOrder={onModifyOrder}
                            onCompleteOrder={actions.onCompleteOrder}
                            onChangeStatusOrder={actions.onChangeStatusOrder}
                            onSendMessageTicket={actions.onSendMessageTicket}
                        />
                    )}
                </Stack>
            </Grid>
            {/* Re-impresión del recibo desde la orden persistida (fecha real de la orden) */}
            {currentOrder && (
                <ModalShowTicket
                    modalShowTicket={modalShowTicket}
                    customer={currentOrder.customer}
                    products={currentOrder.products || []}
                    subtotal={currentOrder.subtotal}
                    promotion={currentOrder.promotion}
                    shippingCost={currentOrder.shippingCost}
                    points={currentOrder.pointsAmount}
                    total={currentOrder.total}
                    date={toDateSafe(currentOrder.date)}
                    // La orden ya existe: con folio el lienzo sale como "Recibo" y
                    // muestra propina y cómo se pagó (D6)
                    shortId={currentOrder.shortId}
                    channelLabel={getOrderChannel(currentOrder).label}
                    tipAmount={currentOrder.tipAmount}
                    cashAmount={getOrderPaidBy(currentOrder, PaymentMethod.Cash)}
                    creditCardAmount={getOrderPaidBy(currentOrder, PaymentMethod.CreditCard)}
                    bankTransferAmount={getOrderPaidBy(currentOrder, PaymentMethod.BankTransfer)}
                />
            )}
            {/* 🚨 El cobro también en la LISTA. Hasta el 2026-09-29 solo se montaba en el
            tablero: "Completar pedido" del detalle, en un pedido que debía dinero, abría
            un modal que no estaba en pantalla y no pasaba nada (el pendiente se quedaba
            en el ref y el modal brincaba después, al cambiar al tablero). */}
            <ModalRegisterPayment
                modalRegisterPayment={actions.modalRegisterPayment}
                order={actions.pedidoPorCobrar?.order}
                onRegister={actions.onRegisterPayment}
                onSkip={actions.onSkipPayment}
            />
            <ModalConfirmation modalConfirmation={actions.modalCancel} onConfirmation={actions.onCancelOrder} />
            <ModalUberQuote
                isOpen={uber.isQuoteModalOpen}
                onClose={uber.onCloseQuoteModal}
                onConfirm={uber.onConfirmUberDelivery}
                quoteData={uber.quoteData}
                isLoading={false}
            />
        </Grid>
    );
};

export default ListOrdersScreen;
